import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Save, Trash2, Upload, Building2, MapPin, ImageIcon, Loader2 } from "lucide-react";

import { api, formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const STRING_FIELDS = ["name", "phone", "email", "website", "vat_number", "registration_number"];
const ADDRESS_FIELDS = ["line1", "line2", "city", "postcode", "country"];

function emptyDoc() {
  return {
    name: "",
    phone: "",
    email: "",
    website: "",
    vat_number: "",
    registration_number: "",
    logo_file_id: null,
    address: { line1: "", line2: "", city: "", postcode: "", country: "" },
  };
}

export default function CompanyProfile() {
  const { hasPermission } = useAuth();
  const { refresh: refreshCompanyContext } = useCompany();
  const navigate = useNavigate();
  const canEdit = hasPermission("settings.company.write");

  const [form, setForm] = useState(emptyDoc());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/company");
        setForm({
          name: data.name || "",
          phone: data.phone || "",
          email: data.email || "",
          website: data.website || "",
          vat_number: data.vat_number || "",
          registration_number: data.registration_number || "",
          logo_file_id: data.logo_file_id || null,
          address: {
            line1: (data.address && data.address.line1) || "",
            line2: (data.address && data.address.line2) || "",
            city: (data.address && data.address.city) || "",
            postcode: (data.address && data.address.postcode) || "",
            country: (data.address && data.address.country) || "",
          },
        });
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setStr = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setAddr = (k) => (e) =>
    setForm((f) => ({ ...f, address: { ...f.address, [k]: e.target.value } }));

  const handleSave = async () => {
    if (!canEdit) return;
    if (!form.name.trim()) {
      toast.error("Company name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { address: { ...form.address } };
      for (const k of STRING_FIELDS) payload[k] = form[k] || null;
      for (const k of ADDRESS_FIELDS) payload.address[k] = form.address[k] || null;
      payload.name = form.name.trim();
      await api.patch("/settings/company", payload);
      toast.success("Company profile saved");
      await refreshCompanyContext();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setLogoPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/company/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, logo_file_id: data.logo_file_id }));
      setLogoPreview(null);
      toast.success("Logo uploaded");
      await refreshCompanyContext();
    } catch (err) {
      toast.error(formatApiError(err));
      setLogoPreview(null);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleLogoDelete = async () => {
    if (!form.logo_file_id) return;
    if (!window.confirm("Remove the current logo?")) return;
    try {
      await api.delete("/settings/company/logo");
      setForm((f) => ({ ...f, logo_file_id: null }));
      toast.success("Logo removed");
      await refreshCompanyContext();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const currentLogoUrl = form.logo_file_id
    ? `${API_BASE}/settings/company/logo/public?v=${form.logo_file_id}`
    : null;

  if (loading) {
    return (
      <div className="p-6" data-testid="settings-company-loading">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 pb-24" data-testid="settings-company-page">
      <PageHeader
        testId="settings-company-header"
        title="Company Profile"
        subtitle="Branding and business details used on PDF reports, email footers, and the sign-in screen."
        actions={
          <Button variant="outline" onClick={() => navigate("/settings")} data-testid="settings-company-back">
            Back to Settings
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column — Company details */}
        <Card className="rounded-md">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" /> Company details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field label="Company name" required>
              <Input
                data-testid="settings-company-name-input"
                value={form.name}
                onChange={setStr("name")}
                disabled={!canEdit}
                placeholder="Field Service"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone">
                <Input
                  data-testid="settings-company-phone-input"
                  value={form.phone}
                  onChange={setStr("phone")}
                  disabled={!canEdit}
                  placeholder="+44 …"
                />
              </Field>
              <Field label="Public email">
                <Input
                  data-testid="settings-company-email-input"
                  value={form.email}
                  onChange={setStr("email")}
                  disabled={!canEdit}
                  placeholder="support@…"
                  type="email"
                />
              </Field>
            </div>
            <Field label="Website">
              <Input
                data-testid="settings-company-website-input"
                value={form.website}
                onChange={setStr("website")}
                disabled={!canEdit}
                placeholder="https://…"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="VAT number">
                <Input
                  data-testid="settings-company-vat-input"
                  value={form.vat_number}
                  onChange={setStr("vat_number")}
                  disabled={!canEdit}
                />
              </Field>
              <Field label="Registration number">
                <Input
                  data-testid="settings-company-reg-input"
                  value={form.registration_number}
                  onChange={setStr("registration_number")}
                  disabled={!canEdit}
                />
              </Field>
            </div>
          </CardContent>
        </Card>

        {/* Right column — Address + Logo */}
        <div className="space-y-6">
          <Card className="rounded-md">
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" /> Registered address
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Field label="Address line 1">
                <Input
                  data-testid="settings-company-addr-line1-input"
                  value={form.address.line1}
                  onChange={setAddr("line1")}
                  disabled={!canEdit}
                />
              </Field>
              <Field label="Address line 2">
                <Input
                  data-testid="settings-company-addr-line2-input"
                  value={form.address.line2}
                  onChange={setAddr("line2")}
                  disabled={!canEdit}
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="City">
                  <Input
                    data-testid="settings-company-addr-city-input"
                    value={form.address.city}
                    onChange={setAddr("city")}
                    disabled={!canEdit}
                  />
                </Field>
                <Field label="Postcode">
                  <Input
                    data-testid="settings-company-addr-postcode-input"
                    value={form.address.postcode}
                    onChange={setAddr("postcode")}
                    disabled={!canEdit}
                  />
                </Field>
              </div>
              <Field label="Country">
                <Input
                  data-testid="settings-company-addr-country-input"
                  value={form.address.country}
                  onChange={setAddr("country")}
                  disabled={!canEdit}
                />
              </Field>
            </CardContent>
          </Card>

          <Card className="rounded-md">
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-primary" /> Logo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div
                className="flex items-center justify-center h-32 border border-dashed border-border rounded-sm bg-muted/30"
                data-testid="settings-company-logo-preview"
              >
                {logoPreview ? (
                  <img src={logoPreview} alt="preview" className="max-h-28 max-w-full object-contain" />
                ) : currentLogoUrl ? (
                  <img src={currentLogoUrl} alt="company logo" className="max-h-28 max-w-full object-contain" data-testid="settings-company-logo-image" />
                ) : (
                  <span className="text-xs text-muted-foreground italic" data-testid="settings-company-logo-empty">No logo uploaded</span>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                PNG or JPEG, 200×200px minimum, up to 2 MB. Auto-resized to 800px longest edge.
              </div>
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                  data-testid="settings-company-logo-file-input"
                />
                {canEdit && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => inputRef.current?.click()}
                      disabled={uploading}
                      data-testid="settings-company-logo-upload-btn"
                    >
                      {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                      <span className="ml-1">{form.logo_file_id ? "Replace logo" : "Upload logo"}</span>
                    </Button>
                    {form.logo_file_id && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleLogoDelete}
                        data-testid="settings-company-logo-delete-btn"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Remove
                      </Button>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {canEdit && (
        <div className="fixed bottom-0 left-56 right-0 bg-background border-t border-border p-3 flex justify-end gap-2 z-30" data-testid="settings-company-actions-bar">
          <Button variant="outline" onClick={() => navigate("/settings")} data-testid="settings-company-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="settings-company-save-btn"
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            <span className="ml-1">Save changes</span>
          </Button>
        </div>
      )}

      {!canEdit && (
        <div className="text-xs text-muted-foreground italic" data-testid="settings-company-readonly-notice">
          You have read-only access to the company profile.
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      {children}
    </div>
  );
}
